<template>
    <div v-drop-zone="validateDrop">
        <div class="search-area">
            <div class="search-row f -a-center">
                <div class="form-group expand">
                    <label for="clients-search"
                           class="sr-only">
                        Chercher un membre
                    </label>
                    <div class="input-group">
                        <div class="input-group-addon">
                            <loader v-show="docsLoading"></loader>
                            <i v-show="!docsLoading" class="glyphicon glyphicon-search"></i>
                        </div>
                        <input type="text"
                               class="form-control"
                               id="clients-search"
                               v-model="docsSearchQuery"
                               placeholder="Nom de document...">
                    </div>
                </div>
            </div>

            <div>
                <div class="form-group">
                    <radio-group :value.sync="docsArchive" type="primary">
                        <radio-btn value="no" checked>Valides</radio-btn>
                        <radio-btn value="yes">Archivés</radio-btn>
                        <radio-btn value="both">Les deux</radio-btn>
                    </radio-group>
                    <radio-group :value.sync="status" type="primary">
                        <radio-btn value="invalid" checked>Invalides</radio-btn>
                        <radio-btn value="valid">Valides</radio-btn>
                        <radio-btn value="todo">TODO</radio-btn>
                        <radio-btn value="all">Tout</radio-btn>
                    </radio-group>
                </div>

                <checkbox-group :value.sync="docsContacts" type="default">
                    <checkbox-btn :value="contact.id.toString()"
                                  v-for="contact in client.contacts">
                        {{ contact.first_name }} {{ contact.last_name }}
                    </checkbox-btn>
                </checkbox-group>
            </div>
        </div>

        <div class="jumbotron"
             v-if="!docHasContent">
            <h2>Aucun document</h2>

            <p>Ce membre n'a encore aucun document. Vous pouvez en ajouter en faisant glisser
                les documents sur cette zone ou en utilisant le bouton d'ajout de documents
                en haut à droite.</p>

            <p class="cta">
                <a @click.prevent="chooseDocumentFile"
                   class="btn btn-lg btn-primary">
                    <i class="glyphicon glyphicon-plus"></i> Ajouter un document
                </a>
            </p>
        </div>

        <div class="jumbotron"
             v-if="displayNoResults">
            <h2>Aucun résultat</h2>

            <p>Il n'y a pas de documents qui corresponde à votre recherche. Essayez avec
                d'autres critères ?</p>
        </div>

        <div class="row -documents"
             v-if="docHasContent">
            <div class="col-sm-6 col-md-4 box"
                 v-for="box in docBoxes">
                <document-upload-box :file="box.content"
                                     v-if="box.thing === 'upload'"></document-upload-box>
                <document-box :document="box.content" :is-protocol="isProtocol"
                              v-if="box.thing === 'doc'"></document-box>
            </div>
        </div>

        <loader v-show="docsLoading" class="bigger-loader"></loader>

        <div class="load-more">
            <a href="#"
               v-if="docsHasNext && !docsLoading"
               v-on:click.prevent="updateDocList(true)"
               class="btn btn-default">
                Charger plus
            </a>
        </div>
    </div>
</template>

<style lang="less"
       scoped>
    @import '~src/style/variables';

    .search-area {
        margin-bottom: @grid-gutter-width;
    }

    .row.-documents > .box {
        float: none;
        display: inline-block;
        vertical-align: top;
    }

    .load-more {
        margin: @grid-gutter-width 0;
        text-align: center;
    }
</style>

<script type="text/ecmascript-6">
    import Loader from '../../components/Loader';
    import RadioGroup from 'vue-strap/src/radioGroup';
    import RadioBtn from 'vue-strap/src/radioBtn';
    import CheckboxGroup from 'vue-strap/src/checkboxGroup';
    import CheckboxBtn from 'vue-strap/src/checkboxBtn';
    import DocumentBox from './DocumentBox';
    import DocumentUploadBox from './DocumentUploadBox';
    import dropZone from '../../directives/drop-zone';
    import {items, makeUpdateList, undefineNull} from '../../utils';
    import {
            list as listDocuments,
            create as createDocument
    } from '../../api/documents';
    import {
        list as listProtocols,
        create as createProtocol
    } from '../../api/protocols';
    import config from '../../config';
    import {upload} from '../../api/files';
    import {manager as msgManager} from '../../messages';

    export default {
        components: {
            Loader,
            RadioGroup,
            RadioBtn,
            CheckboxGroup,
            CheckboxBtn,
            DocumentBox,
            DocumentUploadBox
        },
        props: {
            client: {
                'default': () => ({})
            },
            contract: {
                'default': () => ({})
            },
            company: {
                default: () => ({})
            },
            isProtocol: {
                type: Boolean,
                default: false
            }
        },
        directives: {
            dropZone
        },
        data() {
            return {
                docsSearchQuery: this.$route.params.docName || '',
                docsLoading: false,
                docsPage: 1,
                docsHasNext: false,
                docsList: [],
                docsArchive: 'no',
                status: 'all',
                docsContacts: [],
                uploadQueue: [],
                input: undefined
            };
        },
        computed: {
            docBoxes() {
                return [].concat(
                        this.uploadQueue.map((u) => ({thing: 'upload', content: u})).reverse(),
                        this.docsList.map((d) => ({thing: 'doc', content: d}))
                );
            },
            docHasContent() {
                return this.docsLoading ||
                        this.docsList.length !== 0 ||
                        this.docsSearchQuery !== '' ||
                        this.docsArchive !== 'no' ||
                        this.docsContacts.length !== 0 ||
                        this.uploadQueue.length !== 0;
            },
            displayNoResults() {
                return this.docHasContent && this.docBoxes.length === 0;
            },
            fetchQuery() {
                const query = {
                    archived: this.docsArchive,
                    status: this.status,
                    q: this.docsSearchQuery,
                    contracts: this.docsContacts,
                    all: 1
                };

                if (this.client.id) {
                    query.client = this.client.id;
                } else if (this.contract.id) {
                    query.contract = this.contract.id;
                } else if (this.company.id) {
                    query.company = this.company.id;
                }

                return query;
            }
        },
        methods: {
            validateDrop(dt) {
                const allowed = config.ALLOWED_DOCUMENT_TYPES;

                if (!Array.from(dt.types).some((t) => t === 'Files')) {
                    return false;
                }

                if (Array.from(dt.files || []).some((f) => !allowed.hasOwnProperty(f.type))) {
                    const a = Array.from(items(allowed)).map(([, v]) => v).join(', ');

                    msgManager.push(
                            'Attention.',
                            `Type de fichier non autorisé. Attendu: ${a}`,
                            'warning'
                    );

                    return false;
                }

                return true;
            },
            resetDocList: async function () {
                this.docsPage = 1;
                await this.updateDocList();
            },
            updateDocList: makeUpdateList({
                loading: 'docsLoading',
                page: 'docsPage',
                hasNext: 'docsHasNext',
                list: 'docsList',
                fetch: async function (page) {
                    const list = this.isProtocol ? listProtocols : listDocuments;
                    const out = await list(Object.assign({page}, this.fetchQuery));
                    out.results = out.results.map((d) => undefineNull(d));
                    return out;
                }
            }),
            handleFileChange() {
                this.$emit('file-drop', this.input.files[0]);
            },
            resetInput() {
                if (this.input) {
                    this.input.removeEventListener('change', this.handleFileChange);
                    document.body.removeChild(this.input);
                    this.input = undefined;
                }

                this.input = document.createElement('INPUT');
                this.input.setAttribute('type', 'file');
                this.input.style.display = 'none';
                this.input.addEventListener('change', this.handleFileChange);

                document.body.appendChild(this.input);
            },
            chooseDocumentFile: function () {
                this.input.click();
            }
        },
        watch: {
            uploadQueue: {
                deep: true,
                handler: async function () {
                    if (!this.uploadQueue.length || this.uploadQueue.some((i) => i.uploading)) {
                        return;
                    }

                    const file = this.uploadQueue[0];
                    file.uploading = true;

                    try {
                        const info = await upload(file.file, (p) => {
                            file.progress = p;
                        });

                        if (!info.json) {
                            throw new Error();
                        }

                        const doc = {
                            file: info.json
                        };

                        if (this.client.id) {
                            doc.client = this.client.id;
                        } else if (this.contract.id) {
                            doc.contract = this.contract.id;
                        }
                        if (this.company) {
                            doc.company = this.company.id;
                        }
                        // manual documents (from Gescomi) are automatically VALID
                        doc.status = 'valid';

                        const create = this.isProtocol ? createProtocol : createDocument;
                        await create(doc);
                        await this.resetDocList();
                    } catch (e) {
                        msgManager.push(
                            'Erreur.',
                            `Impossible d'envoyer ${file.file.name}, merci d'essayer à nouveau ` +
                            `plus tard`,
                            'danger'
                        );
                    } finally {
                        this.uploadQueue = this.uploadQueue.filter((f) => f !== file);
                    }
                }
            },
            docsSearchQuery() {
                this.updateDocList();
            },
            docsArchive() {
                this.updateDocList();
            },

            status() {
                this.updateDocList();
            },
            docsContacts() {
                this.updateDocList();
            }
        },
        events: {
            'file-drop': function (file) {
                this.uploadQueue.push({
                    uploading: false,
                    progress: 0,
                    file
                });
            },
            'choose-document-file': function () {
                this.chooseDocumentFile();
            },
            'search-document': function (name) {
                this.docsSearchQuery = name;
            }
        },
        ready() {
            this.resetInput();
            this.updateDocList();
        }
    };
</script>



// WEBPACK FOOTER //
// DocumentsTab.vue?0f83e205